/**
 * Utility functions for WebSocket implementation
 */

/**
 * Ring buffer implementation for efficient message storage
 * Avoids array splicing and provides O(1) insert/access
 */
export class RingBuffer<T> {
  private buffer: (T | undefined)[];
  private writeIndex = 0;
  private size = 0;
  private capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.buffer = new Array(capacity);
  }

  push(item: T): void {
    this.buffer[this.writeIndex] = item;
    this.writeIndex = (this.writeIndex + 1) % this.capacity;

    if (this.size < this.capacity) {
      this.size++;
    }
  }

  getRecent(count: number): T[] {
    const result: T[] = [];
    const actualCount = Math.min(count, this.size);

    for (let i = 0; i < actualCount; i++) {
      const index = (this.writeIndex - 1 - i + this.capacity) % this.capacity;
      const item = this.buffer[index];
      if (item !== undefined) {
        result.unshift(item);
      }
    }

    return result;
  }

  clear(): void {
    this.buffer = new Array(this.capacity);
    this.writeIndex = 0;
    this.size = 0;
  }

  getSize(): number {
    return this.size;
  }
}

/**
 * Debounced function with cancel support
 */
export interface DebouncedFunction<T extends (...args: unknown[]) => unknown> {
  (...args: Parameters<T>): void;
  cancel: () => void;
}

/**
 * Debounce function with TypeScript support and cancel method
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
): DebouncedFunction<T> {
  let timeoutId: NodeJS.Timeout | null = null;

  const debouncedFn = (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };

  debouncedFn.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFn;
}

/**
 * Create a throttled function that limits execution frequency
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  let lastArgs: Parameters<T> | null = null;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func(...lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };
}

import type { TypedEchoInstance } from './types/echo.types';

/**
 * Type guard for Ably Echo instance
 * Checks if Echo instance has a valid Ably connection
 */
export function hasAblyConnection(echo: unknown): echo is TypedEchoInstance {
  try {
    // Dynamic type checking for Echo instance with Ably connector
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const e = echo as any;
    return !!(
      e?.connector?.ably?.connection &&
      typeof e.connector.ably.connection.state === 'string'
    );
  } catch {
    return false;
  }
}

/**
 * Single reader for the Ably connection state behind Echo's `connector.ably`
 * private API. Every caller used to inline its own cast of the same path
 * (connection store, visibility handler, L3 reconcile), so a shape change in
 * Echo would have had to be chased through all of them. Returns null when the
 * path is gone — callers degrade instead of throwing.
 */
export function getAblyConnectionState(echo: unknown): string | null {
  return hasAblyConnection(echo) ? echo.connector.ably.connection.state : null;
}

/**
 * States Ably cannot transparently recover from on its own. `failed`/`closed`
 * are terminal; `suspended` means the >2min drop already lost message
 * continuity, so a fresh attach is the intended recovery path. `disconnected`
 * and `connecting` are deliberately NOT here — Ably resumes those WITH
 * continuity, and tearing them down replays the rewind backlog.
 */
export function isTerminalAblyState(state: string | null): boolean {
  return state === 'failed' || state === 'closed' || state === 'suspended';
}
